import { useQuery } from "@tanstack/react-query";
import { getFleetByCity } from "@/v2/apis/reservations/apis";
import { queryKeys } from "@/v2/queries/keys";
import { useCityWhitelabelMapping } from "@/v2/stores";
import { useAdminDetails } from "./useAuth";

/**
 * Resolves the organization ID for a whitelabel by looking up the first city's
 * fleet.
 *
 * A query rather than an effect, for three reasons that all bite the same way:
 * the answer is shared by every consumer on the page and would otherwise be
 * fetched once per mounted hook; it never changes while the whitelabel is
 * selected, so it should be cached; and an effect cannot tell a caller whether
 * `null` means "still looking" or "no organization" — which for a feature gated
 * per organization is the difference between a spinner and a wrong empty state.
 * Prefer `useOrganizationIdStatus` anywhere that distinction is visible.
 */
export function useOrganizationIdStatus(whitelabelId: number | null): {
  organizationId: string | null;
  isLoading: boolean;
} {
  const cityWhitelabelMapping = useCityWhitelabelMapping();
  const admin = useAdminDetails();

  const firstCityId =
    admin?.cityId != null
      ? admin.cityId
      : whitelabelId == null || !cityWhitelabelMapping
        ? null
        : (cityWhitelabelMapping.find((m) => m.id === whitelabelId)?.cities?.[0]
            ?.id ?? null);

  const { data, isLoading } = useQuery({
    queryKey:
      firstCityId != null
        ? queryKeys.organizations.fleetByCity(firstCityId)
        : ["organizations", "fleetByCity", "disabled"],
    queryFn: () => getFleetByCity(firstCityId!),
    enabled: firstCityId != null,
    // The whitelabel→organization mapping is configuration, not state. Refetching
    // it on every remount would cost a round trip per navigation to answer the
    // same question.
    staleTime: 10 * 60 * 1000,
  });

  // Three different nulls, and a caller that conflates them renders the wrong
  // thing: no whitelabel selected (not loading — say "pick one"), the mapping
  // still arriving from the store (loading), and a whitelabel whose cities are
  // known and empty (not loading — there is genuinely no organization).
  const mappingPending = whitelabelId != null && !cityWhitelabelMapping;

  return {
    organizationId: data?.organization_id ?? null,
    isLoading: mappingPending || (firstCityId != null && isLoading),
  };
}

export function useOrganizationId(whitelabelId: number | null): string | null {
  return useOrganizationIdStatus(whitelabelId).organizationId;
}
